import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { ArrowRight, Clock } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MiniLeadForm from "@/components/MiniLeadForm";
import usePageSEO from "@/hooks/usePageSEO";

export default function Article() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiGet(`/articles/${slug}`),
      apiGet("/products"),
      apiGet("/services"),
    ]).then(([ar, pr, sv]) => {
      if (cancelled) return;
      setA(ar);
      setProducts(pr || []);
      setServices(sv || []);
    }).catch(() => { if (!cancelled) setError("Article not found."); });
    return () => { cancelled = true; };
  }, [slug]);

  usePageSEO(a ? {
    title: a.title,
    description: a.excerpt,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: a.title,
      description: a.excerpt,
      datePublished: a.published_at,
      articleSection: a.category,
    },
  } : { title: "Article", description: "Learning hub article" });

  if (error) return <Err msg={error} />;
  if (!a) return <Load />;

  const related = (a.related_product_ids || [])
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean);
  const relatedServices = (a.related_service_slugs || [])
    .map((slug) => services.find((s) => s.slug === slug))
    .filter(Boolean);
  const disclosures = a.compliance_disclosures || [];

  // Parse simple markdown-ish body (## headings, paragraphs, lists)
  const renderBody = (text) => {
    const blocks = text.split("\n\n");
    const nodes = [];
    blocks.forEach((block, i) => {
      const trimmed = block.trim();
      if (!trimmed) return;
      // Heading first line then rest is paragraph
      if (trimmed.startsWith("## ")) {
        const firstNewline = trimmed.indexOf("\n");
        const headingText = firstNewline === -1 ? trimmed.slice(3) : trimmed.slice(3, firstNewline);
        const rest = firstNewline === -1 ? "" : trimmed.slice(firstNewline + 1).trim();
        nodes.push(<h2 key={`h-${i}`} className="font-serif-display text-2xl text-lw-navy mt-10 mb-3">{headingText}</h2>);
        if (rest) {
          nodes.push(
            <p key={`p-${i}`} className="my-3 leading-relaxed text-sm sm:text-base text-lw-navy/85"
               dangerouslySetInnerHTML={{ __html: rest.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>") }} />
          );
        }
        return;
      }
      if (trimmed.startsWith("- ")) {
        const items = trimmed.split("\n").map((l) => l.replace(/^- /, ""));
        nodes.push(
          <ul key={i} className="my-4 space-y-2 list-disc list-outside ml-5 text-lw-navy/85 text-sm sm:text-base">
            {items.map((it, j) => <li key={j} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: it.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>") }} />)}
          </ul>
        );
        return;
      }
      if (/^\d+\.\s/.test(trimmed)) {
        const items = trimmed.split("\n").map((l) => l.replace(/^\d+\.\s+/, ""));
        nodes.push(
          <ol key={i} className="my-4 space-y-2 list-decimal list-outside ml-5 text-lw-navy/85 text-sm sm:text-base">
            {items.map((it, j) => <li key={j} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: it.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>") }} />)}
          </ol>
        );
        return;
      }
      nodes.push(
        <p key={i} className="my-3 leading-relaxed text-sm sm:text-base text-lw-navy/85"
           dangerouslySetInnerHTML={{ __html: trimmed.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>") }}
        />
      );
    });
    return nodes;
  };

  return (
    <div data-testid={`article-page-${a.slug}`} className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <article className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3 text-sm text-lw-muted">
            <span className="lw-pill bg-lw-navy/5 border-lw-navy/15">{a.category}</span>
            <span className="inline-flex items-center gap-1"><Clock size={12} /> {a.read_time_min} min read</span>
          </div>
          <h1 data-testid="article-title" className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]">{a.title}</h1>
          <p className="mt-4 text-base sm:text-lg text-lw-muted leading-relaxed">{a.excerpt}</p>

          <div data-testid="article-body" className="mt-8 text-lw-navy/85">
            {renderBody(a.body)}
          </div>

          {related.length > 0 && (
            <div className="mt-12 rounded-2xl bg-lw-ivory-warm border border-lw-navy/10 p-6">
              <span className="lw-eyebrow">Related products</span>
              <ul className="mt-3 space-y-1.5" data-testid="article-related">
                {related.map((p) => (
                  <li key={p.id}>
                    <Link to={`/products/${p.id}`} className="text-sm text-lw-navy hover:text-[#8a6a2b] underline decoration-lw-gold/40 underline-offset-4">{p.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {relatedServices.length > 0 && (
            <div className="mt-6 rounded-2xl bg-white border border-lw-navy/10 p-6">
              <span className="lw-eyebrow">Related services</span>
              <ul className="mt-3 space-y-1.5" data-testid="article-related-services">
                {relatedServices.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="text-sm text-lw-navy hover:text-[#8a6a2b] underline decoration-lw-gold/40 underline-offset-4">{s.name} —</Link>
                    <span className="text-sm text-lw-muted"> {s.tagline || s.short_description || ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-12">
            <MiniLeadForm
              source={`article-${a.slug}`}
              title="Talk to an advisor about this"
              subtitle="A free 30-minute call. No pressure, no products on the first call."
              testIdPrefix="article-lead"
            />
          </div>

          {disclosures.length > 0 ? (
            <div data-testid="article-disclosures" className="mt-10 rounded-2xl border border-lw-navy/10 bg-white/70 p-5 space-y-3">
              <span className="lw-eyebrow">Important disclosures</span>
              {disclosures.map((d, i) => (
                <p key={i} className="text-xs text-lw-muted leading-relaxed">{d}</p>
              ))}
              {a.disclaimer && !a.compliance_disclosures?.some((d) => a.disclaimer.includes(d.slice(0, 40))) && (
                <p className="text-xs text-lw-muted leading-relaxed italic">{a.disclaimer.split("\n\n")[0]}</p>
              )}
            </div>
          ) : (
            <p data-testid="article-disclaimer" className="mt-10 text-xs text-lw-muted leading-relaxed">{a.disclaimer}</p>
          )}

          <div className="mt-8">
            <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-lw-navy hover:text-[#8a6a2b]">
              <ArrowRight size={14} className="rotate-180" /> Back to all articles
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}

function Load() { return (<div className="min-h-screen bg-lw-ivory"><Nav /><main className="max-w-3xl mx-auto px-5 pt-32 pb-20"><p className="text-lw-muted">Loading…</p></main><Footer /></div>); }
function Err({ msg }) { return (<div className="min-h-screen bg-lw-ivory"><Nav /><main className="max-w-3xl mx-auto px-5 pt-32 pb-20"><h1 className="font-serif-display text-3xl text-lw-navy">Not found.</h1><p className="mt-3 text-lw-muted">{msg}</p><Link to="/learn" className="btn-gold mt-6 inline-flex">All articles</Link></main><Footer /></div>); }
